import * as React from 'react';

import { cn } from '@/lib/utils';

// =============================================================================
// modal.tsx — Modale accessible générique (Dialog, pattern T-03).
// =============================================================================
// Modale de CONTENU (formulaire) écrite à la main : aucune dépendance ajoutée
// (@radix-ui/react-dialog absent du lockfile figé). Sœur de confirm-dialog.tsx
// (qui reste spécialisé confirmation 2 boutons) ; ce composant porte un contenu
// arbitraire (formulaire multi-champs) et un PIÈGE DE FOCUS complet, requis dès
// qu'une modale contient plusieurs contrôles tabulables (WCAG 2.1.2 / 2.4.3).
//
// WCAG 2.1 AA :
//   - role="dialog" + aria-modal="true" + aria-labelledby (titre) + aria-describedby
//     (description, si fournie) ;
//   - focus déplacé sur le 1er contrôle focusable à l'ouverture, RESTAURÉ sur
//     l'élément déclencheur à la fermeture ;
//   - Tab / Shift+Tab bouclent DANS la modale (piège de focus) ; Échap ferme ;
//   - l'overlay est décoratif (aria-hidden) et ferme au clic.
// Les identifiants (titre/description) viennent de React.useId : deux modales
// simultanées n'entrent pas en collision d'id.

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface ModalProps {
  /** La modale est-elle ouverte ? Fermée = rien dans le DOM. */
  readonly open: boolean;
  /** Fermeture demandée (Échap, clic overlay). L'appelant repasse `open=false`. */
  readonly onClose: () => void;
  /** Titre (obligatoire pour l'étiquetage ARIA). */
  readonly title: string;
  /** Description optionnelle (aria-describedby si fournie). */
  readonly description?: string;
  /** Contenu (formulaire, boutons d'action…). */
  readonly children: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
}: ModalProps): React.JSX.Element | null {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);
  const titleId = React.useId();
  const descId = React.useId();

  React.useEffect(() => {
    if (!open) {
      return;
    }
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const focusables = (): readonly HTMLElement[] => {
      const container = containerRef.current;
      return container === null
        ? []
        : Array.from(
            container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
          );
    };

    // Focus initial : 1er contrôle focusable de la modale (souvent le 1er champ).
    focusables()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      // Piège de focus : boucle Tab / Shift+Tab entre 1er et dernier contrôle.
      const items = focusables();
      if (items.length === 0) {
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay décoratif : ferme au clic, inerte pour les lecteurs d'écran. */}
      <div
        className="absolute inset-0 bg-foreground/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descId}
        className={cn(
          'relative z-10 w-full max-w-lg space-y-4 rounded-lg border border-border',
          'max-h-[85vh] overflow-y-auto bg-card p-6 text-card-foreground shadow-lg',
        )}
      >
        <div className="space-y-1">
          <h2 id={titleId} className="text-lg font-semibold tracking-tight">
            {title}
          </h2>
          {description === undefined ? null : (
            <p id={descId} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
